import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { meals } from '../../../mocks/meals';

const tagConfig: Record<string, { label: string; color: string }> = {
  'High-Protein': { label: 'High Protein', color: 'bg-primary-500' },
  'Vegan':        { label: 'Vegan',        color: 'bg-green-500' },
  'Vegetarian':   { label: 'Vegetarian',   color: 'bg-emerald-500' },
  'Gluten-Free':  { label: 'Gluten-Free',  color: 'bg-yellow-500' },
};

const sliderFoods = meals.map((m) => ({
  id: m.id,
  name: m.name,
  calories: `${m.calories} kcal`,
  price: `€${m.price.toFixed(2)}`,
  image: m.image,
  tag: tagConfig[m.tags[0]]?.label ?? m.tags[0],
  tagColor: tagConfig[m.tags[0]]?.color ?? 'bg-primary-500',
}));

const VISIBLE = 3;
const INTERVAL = 2800;

const SERVICE_CARDS = [
  {
    tab: 0,
    icon: 'ri-home-heart-line',
    label: 'Kita & Kindergarten',
    age: 'Ages 1–6',
    desc: 'Tiny portions, big love',
    accent: 'from-primary-500/80 to-primary-600/80',
    border: 'border-primary-400/40',
    tag: 'bg-primary-500',
    dot: 'bg-primary-400',
  },
  {
    tab: 1,
    icon: 'ri-book-open-line',
    label: 'Schools',
    age: 'Ages 6–18',
    desc: 'Brain fuel for champions',
    accent: 'from-lime-500/80 to-green-600/80',
    border: 'border-lime-400/40',
    tag: 'bg-lime-500',
    dot: 'bg-lime-400',
  },
];

export default function Hero() {
  const [isVisible, setIsVisible] = useState(false);
  const [addedId, setAddedId] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [animating, setAnimating] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setActiveIndex((prev) => (prev + 1) % sliderFoods.length);
        setAnimating(false);
      }, 350);
    }, INTERVAL);
  };

  useEffect(() => {
    startTimer();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const handleAdd = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    setAddedId(id);
    setTimeout(() => setAddedId(null), 1500);
  };

  const visibleFoods = Array.from({ length: VISIBLE }, (_, i) =>
    sliderFoods[(activeIndex + i) % sliderFoods.length]
  );

  const goTo = (dir: 1 | -1) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setAnimating(true);
    setTimeout(() => {
      setActiveIndex((prev) => (prev + dir + sliderFoods.length) % sliderFoods.length);
      setAnimating(false);
      startTimer();
    }, 350);
  };

  const scrollToService = (tab: number) => {
    window.dispatchEvent(new CustomEvent('serviceTabSwitch', { detail: { tab } }));
    setTimeout(() => {
      const el = document.getElementById('services-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-primary-50 via-white to-pink-50">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=professional%20food%20photography%20of%20gourmet%20chef%20prepared%20healthy%20meal%20with%20fresh%20vegetables%20and%20grilled%20protein%20on%20elegant%20white%20plate%2C%20top%20view%2C%20natural%20lighting%2C%20minimalist%20clean%20white%20background%2C%20high%20quality%20restaurant%20presentation%2C%20vibrant%20colors%2C%20shallow%20depth%20of%20field%2C%20modern%20culinary%20art&width=1920&height=1080&seq=hero-bg-001&orientation=landscape"
          alt="Fresh prepared meals"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black/70 lg:bg-gradient-to-r lg:from-black/65 lg:via-black/45 lg:to-black/20"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-36 lg:pb-40 lg:pt-32 flex flex-col lg:flex-row items-center justify-between gap-10 lg:gap-12">
        {/* Left: Text */}
        <div className="w-full lg:max-w-2xl lg:flex-shrink-0 text-center lg:text-left">
          <div className={`inline-flex items-center space-x-2 px-4 py-2 bg-primary-500/20 backdrop-blur-sm border border-primary-400/30 rounded-full mb-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <i className="ri-leaf-line text-primary-400"></i>
            <span className="text-xs sm:text-sm font-medium text-white">Fresh Daily | Regional Ingredients</span>
          </div>

          <h1 className={`text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-5 leading-tight transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            Fresh Chef-Prepared Meals{' '}
            <span className="bg-gradient-to-r from-primary-400 to-pink-300 bg-clip-text text-transparent">
              Delivered To Your Door
            </span>
          </h1>

          <p className={`text-base sm:text-lg lg:text-xl text-white/90 mb-8 leading-relaxed transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            Healthy, regional and freshly cooked meals delivered across Germany. No cooking, no cleaning, just delicious food.
          </p>

          <div className={`flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start transition-all duration-700 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <button className="px-7 py-3.5 sm:px-8 sm:py-4 bg-primary-500 text-white text-base sm:text-lg font-semibold rounded-full hover:bg-primary-600 transition-all shadow-2xl hover:shadow-primary-500/50 hover:scale-105 whitespace-nowrap cursor-pointer">
              Order Meals
            </button>
            <button className="px-7 py-3.5 sm:px-8 sm:py-4 bg-white/10 backdrop-blur-sm text-white text-base sm:text-lg font-semibold rounded-full border-2 border-white/30 hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer">
              For Companies
            </button>
          </div>

          <div className={`flex flex-wrap items-center gap-4 sm:gap-6 mt-8 justify-center lg:justify-start transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="flex items-center space-x-2">
              <i className="ri-checkbox-circle-fill text-primary-400 text-lg"></i>
              <span className="text-white/90 text-xs sm:text-sm">No Commitment</span>
            </div>
            <div className="flex items-center space-x-2">
              <i className="ri-checkbox-circle-fill text-primary-400 text-lg"></i>
              <span className="text-white/90 text-xs sm:text-sm">Cancel Anytime</span>
            </div>
            <div className="flex items-center space-x-2">
              <i className="ri-checkbox-circle-fill text-primary-400 text-lg"></i>
              <span className="text-white/90 text-xs sm:text-sm">Fresh Guarantee</span>
            </div>
          </div>
        </div>

        {/* Right: Sliding Food Cards — vertical on desktop, horizontal scroll on mobile */}
        <div className={`w-full lg:w-72 lg:flex-shrink-0 transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 lg:translate-x-0 translate-y-0' : 'opacity-0 lg:translate-x-12 translate-y-4'}`}>
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-white/70 text-xs font-semibold uppercase tracking-widest">Today's Picks</span>
              <span className="w-2 h-2 rounded-full bg-primary-400 animate-pulse"></span>
            </div>
            <div className="hidden lg:flex items-center gap-1">
              <button
                onClick={() => goTo(-1)}
                className="w-6 h-6 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-up-s-line text-white text-sm"></i>
              </button>
              <button
                onClick={() => goTo(1)}
                className="w-6 h-6 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-down-s-line text-white text-sm"></i>
              </button>
            </div>
          </div>

          {/* Desktop: vertical 3-card slider */}
          <div className="hidden lg:flex flex-col gap-3 overflow-hidden">
            {visibleFoods.map((food, index) => (
              <div
                key={`${food.id}-${activeIndex}-${index}`}
                onClick={() => navigate(`/meal/${food.id}`)}
                className={`bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl overflow-hidden hover:bg-white/20 hover:scale-[1.02] transition-all duration-300 cursor-pointer group ${animating ? 'opacity-0 translate-y-3' : 'opacity-100 translate-y-0'}`}
                style={{ transitionDuration: '350ms', transitionDelay: animating ? '0ms' : `${index * 60}ms` }}
              >
                <div className="relative w-full h-28 overflow-hidden">
                  <img src={food.image} alt={food.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                  <span className={`absolute top-2 left-2 ${food.tagColor} text-white text-xs font-semibold px-2 py-0.5 rounded-full`}>{food.tag}</span>
                </div>
                <div className="p-3 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-white font-semibold text-sm truncate">{food.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-white/60 text-xs flex items-center gap-1"><i className="ri-fire-line text-primary-400"></i>{food.calories}</span>
                      <span className="text-primary-400 font-bold text-sm">{food.price}</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleAdd(e, food.id)}
                    className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap ${addedId === food.id ? 'bg-green-500 scale-110' : 'bg-primary-500 hover:bg-primary-600 hover:scale-110'}`}
                  >
                    <i className={`text-white text-sm ${addedId === food.id ? 'ri-check-line' : 'ri-add-line'}`}></i>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile: horizontal scroll cards */}
          <div className="lg:hidden flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
            {sliderFoods.map((food) => (
              <div
                key={food.id}
                onClick={() => navigate(`/meal/${food.id}`)}
                className="flex-shrink-0 w-44 snap-start bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl overflow-hidden cursor-pointer group"
              >
                <div className="relative w-full h-24 overflow-hidden">
                  <img src={food.image} alt={food.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                  <span className={`absolute top-2 left-2 ${food.tagColor} text-white text-xs font-semibold px-2 py-0.5 rounded-full`}>{food.tag}</span>
                </div>
                <div className="p-2.5 flex items-center justify-between gap-1">
                  <div className="min-w-0">
                    <p className="text-white font-semibold text-xs truncate">{food.name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-white/60 text-xs flex items-center gap-0.5"><i className="ri-fire-line text-primary-400 text-xs"></i>{food.calories}</span>
                      <span className="text-primary-400 font-bold text-xs">{food.price}</span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleAdd(e, food.id)}
                    className={`flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full transition-all duration-300 cursor-pointer ${addedId === food.id ? 'bg-green-500' : 'bg-primary-500 hover:bg-primary-600'}`}
                  >
                    <i className={`text-white text-xs ${addedId === food.id ? 'ri-check-line' : 'ri-add-line'}`}></i>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Dots (desktop only) */}
          <div className="hidden lg:flex items-center justify-center gap-1.5 mt-2">
            {sliderFoods.map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  if (timerRef.current) clearInterval(timerRef.current);
                  setAnimating(true);
                  setTimeout(() => { setActiveIndex(i); setAnimating(false); startTimer(); }, 350);
                }}
                className={`rounded-full transition-all duration-300 cursor-pointer ${i === activeIndex ? 'w-4 h-1.5 bg-primary-400' : 'w-1.5 h-1.5 bg-white/30 hover:bg-white/50'}`}
              />
            ))}
          </div>

          {/* View All */}
          <button
            onClick={() => navigate('/meal/1')}
            className="mt-3 w-full text-center text-white/70 hover:text-primary-400 text-sm font-medium transition-colors cursor-pointer flex items-center justify-center gap-1 whitespace-nowrap"
          >
            View Full Menu <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>

      {/* ── Service Gateway Cards (bottom bridge) ── */}
      <div className={`absolute bottom-0 left-0 right-0 z-20 transition-all duration-700 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        {/* Wavy top fade */}
        <div className="absolute -top-10 left-0 right-0 h-10 bg-gradient-to-b from-transparent to-black/30 pointer-events-none"></div>

        <div className="flex flex-col sm:flex-row items-stretch max-w-3xl mx-auto px-4 gap-0 sm:gap-0">
          {SERVICE_CARDS.map((card, i) => (
            <button
              key={card.tab}
              onClick={() => scrollToService(card.tab)}
              className={`
                flex-1 flex items-center gap-4 px-6 py-5
                bg-gradient-to-br ${card.accent}
                backdrop-blur-md border-t border-x ${card.border}
                ${i === 0 ? 'sm:rounded-tl-2xl sm:border-r-0 rounded-t-2xl sm:rounded-tr-none' : 'sm:rounded-tr-2xl rounded-b-2xl sm:rounded-bl-none'}
                hover:brightness-110 active:scale-[0.98]
                transition-all duration-200 cursor-pointer group
              `}
            >
              {/* Icon bubble */}
              <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${card.tag} flex-shrink-0 group-hover:scale-110 transition-transform duration-200`}>
                <i className={`${card.icon} text-white text-xl`}></i>
              </div>

              {/* Text */}
              <div className="flex-1 text-left min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-white font-bold text-sm truncate">{card.label}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${card.tag} text-white font-medium whitespace-nowrap`}>{card.age}</span>
                </div>
                <p className="text-white/75 text-xs">{card.desc}</p>
              </div>

              {/* Arrow */}
              <div className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-white/20 group-hover:bg-white/35 group-hover:translate-x-1 transition-all duration-200">
                <i className="ri-arrow-down-line text-white text-sm"></i>
              </div>
            </button>
          ))}
        </div>

        {/* Connector line between cards on desktop */}
        <div className="hidden sm:block absolute bottom-0 left-1/2 -translate-x-px w-px h-full bg-white/20 pointer-events-none"></div>
      </div>

      {/* Scroll Indicator — hidden now, replaced by cards */}
    </section>
  );
}
